<template>
    <div class="modal fade show d-block" tabindex="-1" role="dialog">
        <div class="modal-dialog modal-dialog-centered" role="document">
            <div class="modal-content">
                <div class="modal-header">
                    <h5 class="modal-title">
                        <font-awesome-icon :icon="['fas', 'lock']" class="text-primary" />
                        {{ $t('simple.xchat_pin_title') }}
                    </h5>
                    <button type="button" class="close" aria-label="Close" @click="$close(false)">
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>

                <div class="modal-body">
                    <p>{{ $t('simple.xchat_pin_desc') }}</p>

                    <b-form @submit.prevent="submit">
                        <b-form-group :label="$t('simple.xchat_pin_label')" label-for="xchat-pin">
                            <!-- type=password so it is not shoulder-read or captured by the
                                 browser's form history; inputmode=numeric keeps a phone
                                 keypad numeric for what is normally a 4-digit PIN. -->
                            <b-form-input
                                id="xchat-pin"
                                v-model="pin"
                                type="password"
                                inputmode="numeric"
                                autocomplete="off"
                                :disabled="submitting"
                                :state="error ? false : null"
                                ref="pin"
                                @input="error = null; detail = null" />
                        </b-form-group>
                    </b-form>

                    <b-alert variant="danger" :show="!!error">
                        {{ error }}
                        <!-- The attempt count is the whole reason this is not a generic
                             error box: X destroys the account's chat keys once the guesses
                             run out, and there is no way back from that. -->
                        <div v-if="detail" class="mt-2">
                            <small class="text-monospace">{{ detail }}</small>
                        </div>
                        <div v-if="guesses_remaining !== null" class="mt-2">
                            <strong>{{ $t('simple.xchat_pin_attempts', { count: guesses_remaining }) }}</strong>
                            <div><small>{{ $t('simple.xchat_pin_attempts_warning') }}</small></div>
                        </div>
                    </b-alert>

                    <b-alert variant="secondary" show>
                        <small>{{ $t('simple.xchat_pin_privacy') }}</small>
                    </b-alert>
                </div>

                <div class="modal-footer">
                    <b-button variant="secondary" :disabled="submitting" @click="$close(false)">
                        {{ $t('simple.xchat_pin_skip') }}
                    </b-button>
                    <b-button variant="primary" :disabled="submitting || !pin" @click="submit">
                        <font-awesome-icon v-if="submitting" :icon="['fas', 'spinner']" spin />
                        <font-awesome-icon v-else :icon="['fas', 'unlock']" />
                        {{ $t('simple.xchat_pin_submit') }}
                    </b-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: ['account_id'],

    data() {
        return {
            pin: '',
            submitting: false,
            error: null,
            detail: null,
            guesses_remaining: null,
        };
    },

    mounted() {
        this.$nextTick(() => this.$refs.pin && this.$refs.pin.focus());
    },

    methods: {
        async submit() {
            if (!this.pin || this.submitting) {
                return;
            }

            this.submitting = true;
            this.error = null;
            this.detail = null;

            try {
                const { data } = await axios.post('/api/v1/twitter/' + this.account_id + '/xchat-unlock-keys', {
                    pin: this.pin,
                });

                // Resolved with the success message so the page can surface it; the PIN
                // itself is dropped here and never held in the store.
                this.$close(data.message || this.$t('simple.xchat_pin_success'));
            } catch (e) {
                const body = (e.response && e.response.data) || {};

                this.guesses_remaining = typeof body.guesses_remaining === 'number'
                    ? body.guesses_remaining
                    : null;

                this.error = body.message || this.$t('simple.xchat_pin_failed');

                // A failure that carries no wrong-PIN verdict is ours, not theirs: show
                // what actually went wrong instead of implying they mistyped.
                this.detail = body.guesses_remaining === null || body.guesses_remaining === undefined
                    ? (body.detail || null)
                    : null;
                this.pin = '';
                this.$nextTick(() => this.$refs.pin && this.$refs.pin.focus());
            } finally {
                this.submitting = false;
            }
        },
    },
};
</script>
